/* @flow weak */
// typecheck: ignore file
import Activity from 'entities/Activity';
import AmountInput from 'components/AmountInput.jsx';
import Button from 'components/Button.jsx';
import ClickEventTrigger from 'components/ClickEventTrigger.jsx';
import createChainedFunction from 'utils/createChainedFunction';
import EventTrackingConstants from 'payments/constants/EventTrackingConstants';
import FocusEventTrigger from 'components/FocusEventTrigger.jsx';
import Form from 'components/Form.jsx';
import FormCheckBox from 'components/FormCheckBox.jsx';
import FormDatePickerInput from 'components/FormDatePickerInput.jsx';
import FormInput from 'components/FormInput.jsx';
import FormTimePickerInput from 'components/FormTimePickerInput.jsx';
import React, { Component } from 'react';
import Textarea from 'components/Textarea.jsx';

import './styles/ActivityForm.scss';

type Props = {
  loading?: boolean,
  onValid?: Function,
  onInvalid?: Function,
  onValidSubmit?: (values: Object) => void,
  initialActivity?: Activity,
  canCollectMoney?: boolean,
  showLabels?: boolean,
  showEnableReplies?: boolean,
  showTitleField?: boolean,
  showWelcomeMsgBodyField?: boolean,
  showCheckboxFields?: boolean,
  submitText?: string,
  disabled?: boolean,
  onChange?: (values: Object) => void,
};

class ActivityForm extends Component {
  props: Props;

  static defaultProps = {
    loading: false,
    canCollectMoney: true,
    showEnableReplies: false,
    submitText: 'Create',
    showLabels: false,
    showTitleField: true,
    showWelcomeMsgBodyField: true,
    showCheckboxFields: true,
    disabled: false,
  }

  state = {
    submitDisabled: true,
    amount: this.props.initialActivity ? (this.props.initialActivity.getAmountPerPerson() || 0) / 100 : null,
    hasChildren: this.props.initialActivity ? this.props.initialActivity.hasChildren() : false,
    collectOnline: this.props.initialActivity ? this.props.initialActivity.isCollectingMoneyOnline() : true,
    timeInputIsPristine: !this.props.initialActivity, // not pristine when editing
    tomorrow: new Date(new Date().getTime() + 24 * 60 * 60 * 1000), // this is in the state because it would make the render non-pure
  }

  onValid = () => {
    if (this.state.submitDisabled && !this.props.disabled) {
      this.setState({ submitDisabled: false });
    }
  }

  onInvalid = () => {
    if (!this.state.submitDisabled) {
      this.setState({ submitDisabled: true });
    }
  }

  treatTimeValues = (values) => {
    if (values.time && values.date) {
      values.date = new Date(
        values.date.getFullYear(),
        values.date.getMonth(),
        values.date.getDate(),
        values.time.getHours(),
        values.time.getMinutes(),
        values.time.getSeconds(),
        values.time.getMilliseconds()
      );
      delete values.time;
    }

    if (values.end_time && values.date) {
      values.end_date = new Date(
        values.date.getFullYear(),
        values.date.getMonth(),
        values.date.getDate(),
        values.end_time.getHours(),
        values.end_time.getMinutes(),
        values.end_time.getSeconds(),
        values.end_time.getMilliseconds()
      );
      delete values.end_time;
    }
    return values;
  };

  onValidSubmit = (values, resetForm, invalidateForm) => {
    if (!values.has_no_children) {
      values.enable_replies = false;
    }

    // The ui checkbox are inverted from the values
    // since it easier to have checked == true.
    // Here we revert them to they normal names & values.
    values.disable_replies = !values.enable_replies;
    delete values.enable_replies;

    values.has_children = !values.has_no_children;
    delete values.has_no_children;

    const treatedValues = this.treatTimeValues(values);

    if (this.props.onValidSubmit) {
      this.props.onValidSubmit(treatedValues, resetForm, invalidateForm);
    }
  }

  onDateChange = (date) => {
    if (this.state.timeInputIsPristine) {
      const time = new Date(date);
      time.setHours(12, 0, 0); // default to noon
      const endTime = new Date(time.getTime());
      endTime.setHours(time.getHours() + 1);
      this.setState({ timeInputIsPristine: false, time, endTime });
    }
  };

  onChange = (values) => {
    const { onChange } = this.props;
    if (onChange) {
      onChange(this.treatTimeValues(values));
    }
  };

  render() {
    const { tomorrow, hasChildren } = this.state;
    const { initialActivity, disabled } = this.props;


    let title = null;
    let welcomeMsgBody = null;
    let location = null;
    let dateTime = null;
    let endDateTime = null;
    let enableReplies = true;

    if (initialActivity) {
      title = initialActivity.getTitle();
      welcomeMsgBody = initialActivity.getWelcomeMsgBody();
      location = initialActivity.getLocation();
      dateTime = initialActivity.getStartAt();
      endDateTime = initialActivity.getEndAt();
      enableReplies = !initialActivity.hasDisableReplies();
    }

    const time = this.state.time ? this.state.time : dateTime;
    const endTime = this.state.endTime ? this.state.endTime : endDateTime;
    let minDate;

    // when editing an activity, we want the minimun date to be
    // the current activity start at date even if it's in the past.
    if (dateTime && dateTime < tomorrow) {
      minDate = dateTime;
    } else {
      minDate = tomorrow;
    }

    return (
      <Form
        {...this.props}
        onChange={this.onChange}
        onValid={createChainedFunction(this.onValid, this.props.onValid)}
        onInvalid={createChainedFunction(this.onInvalid, this.props.onInvalid)}
        onValidSubmit={this.onValidSubmit}>

        <div className="ActivityForm-row">
          {this.props.showTitleField &&
            <div className="ActivityForm-row-title">
              <FocusEventTrigger
                eventElementName="activity_title"
                eventScreenName={EventTrackingConstants.CREATE_ACTIVITY_FORM_UNIVERSAL_DIALOG}>
                <FormInput
                  label="Title"
                  labelHidden={!this.props.showLabels}
                  name="title"
                  autoFocus
                  autoComplete="off"
                  required="isRequired"
                  placeholder="Title of activity"
                  validations={{ maxLength: 50 }}
                  showErrorAfterBlur
                  validationErrors={{
                    isRequired: 'An activity name is required.',
                    maxLength: 'The activity name can’t be longer than 50 characters.',
                  }}
                  value={title}
                  disabled={disabled}
                />
              </FocusEventTrigger>
            </div>
          }
          {this.props.canCollectMoney &&
            <div className="ActivityForm-row-amount">
              <ClickEventTrigger
                eventScreenName={EventTrackingConstants.CREATE_ACTIVITY_FORM_UNIVERSAL_DIALOG}
                eventElementName="activity_amount">
                <FormInput
                  label="Cost per person"
                  labelHidden={!this.props.showLabels}
                  name="amount"
                  component={AmountInput}
                  placeholder="Cost"
                  value={this.state.amount}
                  onChange={(value) => this.setState({ amount: value })}
                  validations={{
                    isValidEventAmount: 0.99,
                    isLessThan: 2001,
                  }}
                  validationErrors={{
                    isValidEventAmount: 'The minimum amount is $1.',
                    isLessThan: 'The maximum amount is $2000.',
                  }}
                  disabled={disabled}
                />
              </ClickEventTrigger>
            </div>
          }
        </div>

        {this.props.showWelcomeMsgBodyField &&
          <div>
            <ClickEventTrigger
              eventElementName="activity_welcome_msg_body"
              eventScreenName={EventTrackingConstants.CREATE_ACTIVITY_FORM_UNIVERSAL_DIALOG}>
              <FormInput
                label="Welcome message"
                labelHidden={!this.props.showLabels}
                rows={6}
                name="welcome_msg_body"
                component={Textarea}
                placeholder="Welcome message"
                value={welcomeMsgBody}
                disabled={disabled}
              />
            </ClickEventTrigger>
          </div>
        }

        <div className="ActivityForm-row">
          <div>
            <ClickEventTrigger
              eventElementName="activity_date"
              eventScreenName={EventTrackingConstants.CREATE_ACTIVITY_FORM_UNIVERSAL_DIALOG}>
              <FormDatePickerInput
                label="Day"
                labelHidden={!this.props.showLabels}
                data-test="activity-form-date"
                name="date"
                autoComplete="off"
                required="isRequired"
                min={minDate}
                showErrorAfterBlur
                onChange={this.onDateChange}
                validations={{ isGreaterOrEqualToDate: minDate }}
                placeholder="Date"
                validationErrors={{
                  isRequired: 'An activity date is required.',
                  isGreaterOrEqualToDate: 'The activity date can’t be in the past.',
                }}
                value={dateTime}
                disabled={disabled}
              />
            </ClickEventTrigger>
          </div>

          <div>
            <ClickEventTrigger
              eventScreenName={EventTrackingConstants.CREATE_ACTIVITY_FORM_UNIVERSAL_DIALOG}
              eventElementName="activity_time">
              <FormTimePickerInput
                label="Start time"
                labelHidden={!this.props.showLabels}
                data-test="activity-form-time"
                required="isRequired"
                autoComplete="off"
                className="ActivityForm-row-time-input"
                name="time"
                placeholder="Start time"
                showErrorAfterBlur
                value={time}
                disabled={disabled}
              />
            </ClickEventTrigger>
          </div>

          <div>
            <ClickEventTrigger
              eventScreenName={EventTrackingConstants.CREATE_ACTIVITY_FORM_UNIVERSAL_DIALOG}
              eventElementName="activity_end_time">
              <FormTimePickerInput
                label="End time"
                labelHidden={!this.props.showLabels}
                data-test="activity-form-end-time"
                autoComplete="off"
                className="ActivityForm-row-end-time-input"
                name="end_time"
                placeholder="End time"
                showErrorAfterBlur
                value={endTime}
                disabled={disabled}
              />
            </ClickEventTrigger>
          </div>
        </div>

        <ClickEventTrigger
          eventElementName="activity_location"
          eventScreenName={EventTrackingConstants.CREATE_ACTIVITY_FORM_UNIVERSAL_DIALOG}>
          <FormInput
            label="Location"
            labelHidden={!this.props.showLabels}
            data-test="activity-form-location"
            glyph="location-pin"
            name="location"
            placeholder="Location"
            inputClassName="ActivityForm-location-input"
            value={location}
            disabled={disabled}
          />
        </ClickEventTrigger>

        <div className="ActivityForm-footer">
          <div className="ActivityForm-footer-checkboxes">
            {this.props.showCheckboxFields &&
              <div>
                <div>
                  <FormCheckBox
                    defaultChecked={!hasChildren}
                    name="has_no_children"
                    label="All participants are 13 or older"
                    onChange={(value) => this.setState({ hasChildren: !value.currentTarget.checked })}
                    eventScreenName={EventTrackingConstants.CREATE_ACTIVITY_FORM_UNIVERSAL_DIALOG}
                    eventElementName="activity_has_no_children"
                  />
                </div>
                {this.state.amount > 0 &&
                  <div>
                    <FormCheckBox
                      name="collect_online"
                      label="Use Remind to collect funds for this activity"
                      checked={this.state.collectOnline}
                      onChange={() => this.setState({ collectOnline: !this.state.collectOnline })}
                      eventScreenName={EventTrackingConstants.CREATE_ACTIVITY_FORM_UNIVERSAL_DIALOG}
                      eventElementName="activity_collect_online"
                    />
                  </div>
                }
                {this.props.showEnableReplies &&
                  <div>
                    <FormCheckBox
                      className="ActivityForm-footer-replies"
                      disabled={hasChildren}
                      defaultChecked={enableReplies}
                      name="enable_replies"
                      label="Allow participants to reply to the group conversation"
                      eventScreenName={EventTrackingConstants.CREATE_ACTIVITY_FORM_UNIVERSAL_DIALOG}
                      eventElementName="activity_enable_replies"
                    />
                  </div>
                }
              </div>
            }
          </div>

          <div>
            <Button
              data-test="activity-form-submit"
              size="small"
              className="ActivityForm-submit"
              eventScreenName={EventTrackingConstants.CREATE_ACTIVITY_FORM_UNIVERSAL_DIALOG}
              eventElementName="create_activity"
              loading={this.props.loading}
              disabled={this.state.submitDisabled || this.props.disabled}>
              {this.props.submitText}
            </Button>
          </div>
        </div>
      </Form>
    );
  }
}

export default ActivityForm;



// WEBPACK FOOTER //
// ./src/payments/components/ActivityForm.jsx